import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useEmployees, EMPLOYEE_STATUS_LABEL, CONTRACT_TYPE_LABEL, EmployeeStatus, employeeClientName } from "@/api/employees";
import { useAuthStore } from "@/stores/authStore";
import CreateEmployeeModal from "./CreateEmployeeModal";

const STATUS_STYLE: Record<EmployeeStatus, string> = {
  ACTIVO: "bg-green-100 text-green-700",
  INACTIVO: "bg-gray-100 text-gray-500",
  TERMINADO: "bg-red-100 text-red-700",
};

const STATUS_OPTIONS = Object.keys(EMPLOYEE_STATUS_LABEL) as EmployeeStatus[];

export default function EmployeesPage() {
  const [status, setStatus] = useState<EmployeeStatus | "">("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useEmployees({ status: status || undefined, search: search || undefined, pageSize: 100 });
  const employees = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Empleados</h1>
          <p className="text-sm text-gray-500">Nómina interna de la firma y de clientes con nómina tercerizada.</p>
        </div>
        {hasPermission("payroll.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nuevo empleado
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por nombre, documento o cargo..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as EmployeeStatus | "")}>
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {EMPLOYEE_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cargo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Contrato</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Salario base</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && employees.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  No hay empleados que coincidan con los filtros.
                </td>
              </tr>
            )}
            {employees.map((employee) => (
              <tr key={employee.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{employee.fullName}</p>
                  <p className="text-xs text-gray-400">
                    {employee.documentType} {employee.documentNumber}
                  </p>
                </td>
                <td className="px-4 py-3 text-gray-600">{employee.position || "—"}</td>
                <td className="px-4 py-3 text-gray-600">{CONTRACT_TYPE_LABEL[employee.contractType]}</td>
                <td className="px-4 py-3 text-gray-600">
                  {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(
                    Number(employee.baseSalary)
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {employee.client ? (
                    <Link to={`/clients/${employee.client.id}`} className="hover:text-brand-600">
                      {employeeClientName(employee)}
                    </Link>
                  ) : (
                    <span className="text-gray-400">Interno</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[employee.status]}`}>
                    {EMPLOYEE_STATUS_LABEL[employee.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateEmployeeModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
